/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$tiles-teaser-small-content-margin: 24px !default;
$tiles-teaser-tablet-content-margin: 32px !default;
$tiles-teaser-large-content-margin: 40px !default;
$tiles-teaser-x-large-content-margin: 48px !default;
$tiles-teaser-default-shadow: none !default;
$tiles-teaser-default-radius: 0 !default;
$tiles-teaser-drop-shadow: none !default;
$tiles-teaser-drop-shadow-on-hover: none !default;
$tiles-teaser-mobile-radius: 0 !default;
$tiles-teaser-tablet-radius: 0 !default;
$tiles-teaser-desktop-radius: 0 !default;
$tiles-teaser-desktop-large-radius: 0 !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

:host {
  display: block;
  box-shadow: $tiles-teaser-default-shadow;
  border-radius: $tiles-teaser-default-radius;
  overflow: hidden;
}

:host(:not(.no-link)) {
  filter: drop-shadow($tiles-teaser-drop-shadow);
  transition: filter $transition-duration ease-in-out;
}

:host(:not(.no-link):hover) {
  filter: drop-shadow($tiles-teaser-drop-shadow-on-hover);
}

:host([background-color]) {
  ::slotted(eon-ui-image) {
    display: block;
  }
}

::slotted(*:not(eon-ui-image)) {
  transition: color $transition-duration ease-in-out !important;
}

::slotted(eon-ui-headline) {
  --color: var(--textColor) !important; // v(...) helper doesn't work with nested vars
}

::slotted(eon-ui-link) {
  --contentColor: var(--textColor) !important;
  --contentHoverColor: var(--textColor) !important;
  --contentFocusColor: var(--textColor) !important;
}

::slotted(eon-ui-text) {
  --color: var(--textColor) !important;
}

div.#{$prefix}pictogram-wrapper > eon-ui-pictogram {
  transition: color $transition-duration ease-in-out !important;
  --color: var(--textColor) !important;
}

:host(:not(.no-link)[background-color]:hover),
:host([background-color].no-image),
:host(.no-image) {
  ::slotted(eon-ui-image) {
    opacity: 0 !important;
  }

  // Overriding the CSS variables of child components only works if they are defined in the <style> tag of the shadowRoot.
  // IE/Edge don't support ShadowDOM, so we have to remove the existing inline(!) CSS variable declarations on hover in JS (see teaser.tsx).
  // If we don't do that, the inline styles will always win in terms of specificity and the definitions here won't have any effect.
  ::slotted(eon-ui-headline) {
    --color: var(--hoverColor) !important; // v(...) helper doesn't work with nested vars
  }

  ::slotted(eon-ui-link) {
    --contentColor: var(--hoverColor) !important;
    --contentHoverColor: var(--hoverColor) !important;
    --contentFocusColor: var(--hoverColor) !important;
  }

  ::slotted(eon-ui-text) {
    --color: var(--hoverColor) !important;
  }

  div.#{$prefix}pictogram-wrapper > eon-ui-pictogram {
    --color: var(--hoverColor) !important;
  }
}

.#{$prefix}teaser {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  text-decoration: none;
  overflow: hidden;
  border-radius: $tiles-teaser-desktop-large-radius;

  @include for-size(desktop) {
    border-radius: $tiles-teaser-desktop-radius;
  }

  @include for-size(tablet) {
    border-radius: $tiles-teaser-tablet-radius;
  }

  @include for-size(phone) {
    border-radius: $tiles-teaser-mobile-radius;
  }

  ::slotted(eon-ui-image) {
    transition: opacity $transition-duration ease-in-out;
  }

  ::slotted(eon-ui-info-icon) {
    margin-top: 16px;
  }
}

.#{$prefix}teaser.#{$prefix}no-radius {
  border-radius: 0 !important;
}

.#{$prefix}wrapper {
  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
  display: inline-block;
  background-color: v(backgroundColor);
  z-index: 1;

  &.#{$prefix}only-visible-on-hover {
    background-color: v(backgroundColor);
  }

  &.#{$prefix}ratio-2x1 {
    display: flow-root;
    position: relative;
    &::before {
      @include aspect-ratio-tiles-teaser(2, 1);
    }
  }

  &.#{$prefix}ratio-16x9 {
    display: flow-root;
    position: relative;
    &::before {
      @include aspect-ratio-tiles-teaser(16, 9);
    }
  }

  &.#{$prefix}ratio-1x1 {
    display: flow-root;
    position: relative;
    &::before {
      @include aspect-ratio-tiles-teaser(1, 1);
    }
  }

  &.#{$prefix}ratio-3x4 {
    display: flow-root;
    position: relative;
    &::before {
      @include aspect-ratio-tiles-teaser(3, 4);
    }
  }

  &.#{$prefix}ratio-4x3 {
    display: flow-root;
    position: relative;
    &::before {
      @include aspect-ratio-tiles-teaser(4, 3);
    }
  }

  /*
    * Aspect ratios for tablet breakpoint
    */
  &.#{$prefix}tablet-ratio-2x1 {
    @include for-size("tablet") {
      &::before {
        @include aspect-ratio-tiles-teaser(2, 1);
      }
    }
  }

  &.#{$prefix}tablet-ratio-16x9 {
    @include for-size("tablet") {
      &::before {
        @include aspect-ratio-tiles-teaser(16, 9);
      }
    }
  }

  &.#{$prefix}tablet-ratio-1x1 {
    @include for-size("tablet") {
      &::before {
        @include aspect-ratio-tiles-teaser(1, 1);
      }
    }
  }

  &.#{$prefix}tablet-ratio-3x4 {
    @include for-size("tablet") {
      &::before {
        @include aspect-ratio-tiles-teaser(3, 4);
      }
    }
  }

  &.#{$prefix}ratio-4x3 {
    @include for-size("tablet") {
      &::before {
        @include aspect-ratio-tiles-teaser(4, 3);
      }
    }
  }

  /*
    * Aspect ratios for tablet breakpoint
    */
  &.#{$prefix}mobile-ratio-2x1 {
    @include for-size("phone") {
      &::before {
        @include aspect-ratio-tiles-teaser(2, 1);
      }
    }
  }

  &.#{$prefix}mobile-ratio-16x9 {
    @include for-size("phone") {
      &::before {
        @include aspect-ratio-tiles-teaser(16, 9);
      }
    }
  }

  &.#{$prefix}mobile-ratio-1x1 {
    @include for-size("phone") {
      &::before {
        @include aspect-ratio-tiles-teaser(1, 1);
      }
    }
  }

  &.#{$prefix}mobile-ratio-3x4 {
    @include for-size("phone") {
      &::before {
        @include aspect-ratio-tiles-teaser(3, 4);
      }
    }
  }

  &.#{$prefix}mobile-4x3 {
    @include for-size("phone") {
      &::before {
        @include aspect-ratio-tiles-teaser(4, 3);
      }
    }
  }

  &.#{$prefix}hover-overlay {
    .#{$prefix}overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.2);
      transition: all $transition-duration ease-in-out;
      visibility: hidden;
      opacity: 0;

      &.#{$prefix}color-shade {
        background-color: v(hoverBackgroundColor);
      }
    }

    &:hover {
      .#{$prefix}overlay {
        visibility: visible;
        opacity: 1;
      }
    }

    .#{$prefix}content {
      z-index: 11;
    }
  }
}

.#{$prefix}content {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;

  &.#{$prefix}vertical-content-alignment {
    &-top {
      justify-content: flex-start;
    }

    &-center {
      justify-content: center;
    }

    &-bottom {
      justify-content: flex-end;
    }

    &-distant {
      justify-content: flex-end;

      .#{$prefix}content-wrapper {
        flex: 1 1 0%;
        justify-content: space-between;
      }

      .#{$prefix}header-wrapper {
        justify-content: flex-start;
        flex: 1;
      }

      .#{$prefix}cta-wrapper {
        justify-content: flex-end;
        flex: 1;
        align-items: flex-start;
      }
    }
  }

  &.#{$prefix}horizontal-content-alignment {
    &-left {
      align-items: flex-start;

      .#{$prefix}content-wrapper {
        align-items: flex-start;
      }
    }

    &-center {
      align-items: center;

      .#{$prefix}content-wrapper {
        align-items: center;
        text-align: center;

        .#{$prefix}pictogram-wrapper {
          align-self: center;
        }

        .#{$prefix}headline-wrapper {
          display: flex;
          justify-content: center;
        }

        .#{$prefix}cta-wrapper {
          align-items: center;
        }
      }
    }

    &-right {
      align-items: flex-end;

      .#{$prefix}content-wrapper {
        align-items: flex-end;
        text-align: right;
        left: initial;

        .#{$prefix}headline-wrapper {
          align-items: flex-end;
        }
      }
    }
  }

  &.#{$prefix}content-alignment {
    &-left {
      align-items: flex-start;
      .#{$prefix}content-wrapper {
        align-items: flex-start;
      }
    }

    &-center,
    &-distant {
      align-items: center;

      .#{$prefix}content-wrapper {
        align-items: center;
        text-align: center;

        .#{$prefix}pictogram-wrapper {
          align-self: center;
        }

        .#{$prefix}cta-wrapper {
          align-items: center;
        }
      }
    }

    &-left,
    &-distant {
      justify-content: flex-end;

      .#{$prefix}content-wrapper {
        flex: 1 1 0%;
      }
      .#{$prefix}header-wrapper {
        justify-content: flex-start;
        flex: 1;
      }
      .#{$prefix}cta-wrapper {
        justify-content: flex-end;
        flex: 1;
        align-items: flex-start;
      }
    }

    &-center {
      justify-content: center;
    }
  }

  .#{$prefix}tiles-teaser-violator {
    position: relative;
    width: 100%;
    display: flex;
    flex: 1 1 100%;
    box-sizing: border-box;

    &.#{$prefix}right {
      justify-content: flex-end;
    }

    &.#{$prefix}center {
      justify-content: center;
    }

    @include for-size(desktop-l-or-larger) {
      padding: 48px 48px 0 48px;
    }

    @include for-size(desktop) {
      padding: 40px 40px 0 40px;
    }

    @include for-size(tablet) {
      padding: 32px 32px 0 32px;
    }

    @include for-size(phone) {
      padding: 24px 24px 0 24px;
    }
  }

  &.#{$prefix}vertical-content-alignment-distant {
    .#{$prefix}tiles-teaser-violator {
      flex: 0;

      &.#{$prefix}right {
        align-self: flex-end;
        flex: 0;
        width: auto;
      }
    }
  }

  .#{$prefix}content-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 0 0 0%;
    margin: $tiles-teaser-x-large-content-margin;
    box-sizing: border-box;

    @include for-size(desktop) {
      margin: $tiles-teaser-large-content-margin;
    }

    @include for-size(tablet) {
      margin: $tiles-teaser-tablet-content-margin;
    }

    @include for-size(phone) {
      margin: $tiles-teaser-small-content-margin;
    }

    .#{$prefix}header-wrapper,
    .#{$prefix}cta-wrapper {
      position: relative;
      display: flex;
      flex-direction: column;
    }
  }
}
@include teaserHeadlinesMargin(".#{$prefix}headline-wrapper");
@include teaserCtaMargin(".#{$prefix}cta-wrapper");
